import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BandaidsIcon,
  BellIcon,
  BirdIcon,
  CaretDownIcon,
  CatIcon,
  CheckIcon,
  DogIcon,
  HouseIcon,
  InfoIcon,
  ListIcon,
  PawPrintIcon,
  PlusIcon,
  QuestionIcon,
  ShieldCheckIcon,
  SmileyIcon,
  SmileySadIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import {
  COLOR_PELAJE,
  ESTADO_MASCOTA,
  SEXO_MASCOTA,
  TAMANO_MASCOTA,
  TIPO_MASCOTA,
} from "@encuentrayayuda/shared/schemas/mascotaOpciones";
import { sanitizarEmail, sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";
import { ahoraLocal, hoyLocal } from "@encuentrayayuda/shared/lib/fechas";
import UbicacionMascotaForm from "./UbicacionMascotaForm.jsx";
import FotosMascotaForm from "./FotosMascotaForm.jsx";
import ConfirmarMascotaForm from "./ConfirmarMascotaForm.jsx";
import Turnstile from "./Turnstile.jsx";
import ModalCamposFaltantes from "./ModalCamposFaltantes.jsx";
import AvisoBorrador from "./AvisoBorrador.jsx";
import ReportePublicado from "./ReportePublicado.jsx";
import { publicarMascota } from "./lib/publicarMascota.js";
import { guardarBorrador, hayBorrador, leerBorrador, limpiarBorrador } from "./lib/borradores.js";

const STORAGE_KEY = "encuentrayayuda:mascota-encontrada:v2";
const STORAGE_KEYS_ANTERIORES = ["encuentrayayuda:mascota-encontrada:informacion"];

const pasos = ["Información", "Ubicación", "Fotos", "Confirmar"];

function clasePaso(numero, pasoActual, maximoPasoAlcanzado) {
  if (numero === pasoActual) return "is-active";
  if (numero < pasoActual) return "is-complete";
  if (numero <= maximoPasoAlcanzado) return "is-available";
  return "";
}

const etiquetasTipo = {
  PERRO: "Perro",
  GATO: "Gato",
  OTRO: "Otra mascota",
};

const iconosTipo = {
  PERRO: DogIcon,
  GATO: CatIcon,
  OTRO: BirdIcon,
};

const etiquetasSexo = {
  MACHO: "Macho",
  HEMBRA: "Hembra",
};

const etiquetasColor = {
  NEGRO: "Negro",
  BLANCO: "Blanco",
  MARRON: "Marrón",
  CAFE_CLARO: "Café claro",
  BEIGE: "Beige",
  CREMA: "Crema",
  DORADO: "Dorado",
  AMARILLO: "Amarillo",
  ROJIZO: "Rojizo",
  GRIS: "Gris",
  CANELA: "Canela",
  ATIGRADO: "Atigrado",
  MANCHADO: "Manchado",
};

const detallesTamano = {
  PERRO: {
    MUY_PEQUENO: ["Muy pequeño / Toy", "0–5 kg"],
    PEQUENO: ["Pequeño", "5–10 kg"],
    MEDIANO: ["Mediano", "10–25 kg"],
    GRANDE: ["Grande", "25–40 kg"],
    MUY_GRANDE: ["Muy grande / Gigante", "+40 kg"],
  },
  GATO: {
    MUY_PEQUENO: ["Muy pequeño", "Menos de 3 kg"],
    PEQUENO: ["Pequeño", "3–4 kg"],
    MEDIANO: ["Mediano", "4–5 kg"],
    GRANDE: ["Grande", "5–7 kg"],
    MUY_GRANDE: ["Muy grande", "+7 kg"],
  },
  OTRO: {
    MUY_PEQUENO: ["Muy pequeño", ""],
    PEQUENO: ["Pequeño", ""],
    MEDIANO: ["Mediano", ""],
    GRANDE: ["Grande", ""],
    MUY_GRANDE: ["Muy grande", ""],
  },
};

const estados = [
  { value: "BIEN", label: "Se ve bien", Icon: SmileyIcon },
  { value: "HERIDA", label: "Herida", Icon: BandaidsIcon },
  { value: "ASUSTADA", label: "Asustada", Icon: SmileySadIcon },
];

const datosIniciales = {
  tipoMascota: "",
  sexo: "",
  tamano: "",
  colorPrincipalPelaje: "",
  caracteristicasParticulares: "",
  estadoMascota: "",
  latEncontro: null,
  lngEncontro: null,
  precisionGps: null,
  direccionHallazgo: "",
  // Los rellena el geocodificador al marcar el punto; si el usuario
  // escribe la direccion a mano quedan null y la tarjeta va sin ubicacion.
  ciudad: null,
  departamento: null,
  puntoReferencia: "",
  // fechaMaxima, no minima: una mascota se encuentra en el pasado. El limite es
  // hoy (no se puede reportar el futuro), y no hay piso — se puede reportar algo
  // hallado hace dias.
  fechaMaxima: hoyLocal(),
  fechaHallazgo: hoyLocal(),
  // La hora actual, no un literal. Con un valor fijo, cualquiera que reporte
  // antes de esa hora manda un momento futuro y el backend responde 400.
  horaHallazgo: ahoraLocal(),
};

/**
 * Arma el cuerpo exacto que espera crearMascotaSchema.
 *
 * El schema del backend es `.strict()`: cualquier clave que no este declarada
 * alli hace fallar el request completo. Por eso esto NO es un spread de `datos`
 * — campos como `precisionGps`, `fechaMinima` o `horaHallazgo` viven solo en el
 * formulario y no deben viajar. Enumerar a mano es a proposito.
 *
 * Los opcionales vacios se omiten en vez de mandarse como "": un enum no acepta
 * cadena vacia y textoLibre exige min(1).
 */
function construirPayload(datos, contacto) {
  const payload = {
    tipo: "ENCONTRADO",
    reportante: {
      nombreReportante: sanitizarTexto(contacto.nombre),
      telefonoReportante: `${contacto.codigoPais}${contacto.telefono}`,
      emailReportante: sanitizarEmail(contacto.email),
    },
    tipoMascota: datos.tipoMascota,
    encontro: { lat: datos.latEncontro, lng: datos.lngEncontro },
    // El backend guarda un solo DateTime; el formulario separa fecha y hora.
    // toISOString convierte desde la zona local del dispositivo, que es la del
    // usuario y por lo tanto la correcta.
    encontradoEn: new Date(
      `${datos.fechaHallazgo}T${datos.horaHallazgo || "00:00"}`,
    ).toISOString(),
  };

  const opcionales = {
    sexo: datos.sexo,
    tamano: datos.tamano,
    colorPrincipalPelaje: datos.colorPrincipalPelaje,
    estadoMascota: datos.estadoMascota,
    caracteristicasParticulares: sanitizarTexto(datos.caracteristicasParticulares),
    direccionHallazgo: sanitizarTexto(datos.direccionHallazgo),
    ciudad: datos.ciudad,
    departamento: datos.departamento,
    puntoReferencia: sanitizarTexto(datos.puntoReferencia),
  };
  for (const [clave, valor] of Object.entries(opcionales)) {
    if (valor) payload[clave] = valor;
  }

  return payload;
}

function cargarBorrador() {
  try {
    const guardado = leerBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
    if (!guardado) return datosIniciales;
    const borrador = { ...datosIniciales, ...JSON.parse(guardado) };
    const hoy = hoyLocal();
    return {
      ...borrador,
      fechaMaxima: hoy,
      // Solo se corrige hacia atras: un borrador de ayer conserva su fecha, pero
      // uno que quedo apuntando al futuro (reloj cambiado, borrador viejo) se
      // trae a hoy para que el backend no lo rechace.
      fechaHallazgo: borrador.fechaHallazgo > hoy ? hoy : borrador.fechaHallazgo,
    };
  } catch {
    return datosIniciales;
  }
}

function SelectorTamano({ tipoMascota, value, onChange }) {
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef(null);
  const opciones = detallesTamano[tipoMascota] ?? detallesTamano.OTRO;
  const seleccionado = value ? opciones[value] : null;

  useEffect(() => {
    function cerrarAlHacerClickFuera(evento) {
      if (!contenedorRef.current?.contains(evento.target)) setAbierto(false);
    }
    document.addEventListener("pointerdown", cerrarAlHacerClickFuera);
    return () => document.removeEventListener("pointerdown", cerrarAlHacerClickFuera);
  }, []);

  return (
    <div className="size-picker" ref={contenedorRef}>
      <button
        className="form-select size-picker__trigger"
        type="button"
        onClick={() => setAbierto((actual) => !actual)}
        aria-expanded={abierto}
        aria-controls="opciones-tamano"
        disabled={!tipoMascota}
      >
        <span>{seleccionado ? `${seleccionado[0]} · ${seleccionado[1]}` : tipoMascota ? "Selecciona el tamaño aproximado" : "Primero selecciona el tipo de mascota"}</span>
        <CaretDownIcon weight="bold" />
      </button>

      {abierto && (
        <div className="size-picker__options" id="opciones-tamano" role="listbox" aria-label="Tamaño aproximado">
          {TAMANO_MASCOTA.map((tamano) => {
            const [nombre, peso] = opciones[tamano];
            const activo = value === tamano;
            return (
              <button
                type="button"
                className={activo ? "size-option is-selected" : "size-option"}
                role="option"
                aria-selected={activo}
                onClick={() => { onChange(tamano); setAbierto(false); }}
                key={tamano}
              >
                <span className="size-option__symbol" aria-hidden="true">{nombre.charAt(0)}</span>
                <span><strong>{nombre}</strong>{peso && <small>{peso}</small>}</span>
                {activo && <CheckIcon weight="bold" />}
              </button>
            );
          })}
          <button
            type="button"
            className={!value ? "size-option is-selected" : "size-option"}
            role="option"
            aria-selected={!value}
            onClick={() => { onChange(""); setAbierto(false); }}
          >
            <span className="size-option__symbol size-option__symbol--unknown" aria-hidden="true"><QuestionIcon weight="bold" /></span>
            <span><strong>No especificado</strong><small>No sé el tamaño aproximado</small></span>
            {!value && <CheckIcon weight="bold" />}
          </button>
        </div>
      )}
      <p className="field-help"><InfoIcon weight="bold" /> Si no conoces el peso exacto, elige el rango que más se aproxime.</p>
    </div>
  );
}

function MascotaEncontradaForm({ onBack }) {
  const [datos, setDatos] = useState(cargarBorrador);
  /// Se resuelve una sola vez, al montar: si el formulario arranco con un
  /// borrador, hay que decirlo. Datos que el usuario no acaba de escribir no
  /// pueden aparecer sin explicacion.
  const [borradorRestaurado, setBorradorRestaurado] = useState(() => hayBorrador(STORAGE_KEY));
  const [fotos, setFotos] = useState([]);
  const [contacto, setContacto] = useState({ nombre: "", codigoPais: "57", telefono: "", email: "" });
  const fotosRef = useRef(fotos);
  const [pasoActual, setPasoActual] = useState(1);
  const [maximoPasoAlcanzado, setMaximoPasoAlcanzado] = useState(1);
  const [advertencia, setAdvertencia] = useState(null);
  const [publicando, setPublicando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [tokenAntiBot, setTokenAntiBot] = useState(null);
  const turnstileRef = useRef(null);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const publicacionRef = useRef({ reporte: null, multimedia: [] });

  useEffect(() => {
    const { latEncontro, lngEncontro, precisionGps, ...borradorPersistente } = datos;
    guardarBorrador(STORAGE_KEY, borradorPersistente);
  }, [datos]);

  useEffect(() => {
    fotosRef.current = fotos;
  }, [fotos]);

  useEffect(() => () => {
    fotosRef.current.forEach((foto) => URL.revokeObjectURL(foto.url));
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pasoActual]);

  function descartarBorrador() {
    limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
    setDatos(datosIniciales);
    setBorradorRestaurado(false);
  }

  function cambiarPaso(nuevoPaso) {
    if (nuevoPaso > maximoPasoAlcanzado) return;
    setPasoActual(nuevoPaso);
  }

  function avanzarPaso(nuevoPaso) {
    setMaximoPasoAlcanzado((actual) => Math.max(actual, nuevoPaso));
    setPasoActual(nuevoPaso);
  }

  function mostrarCamposFaltantes(campos, paso) {
    setAdvertencia({ campos, paso });
  }

  function revisarCamposFaltantes() {
    const paso = advertencia?.paso ?? pasoActual;
    setAdvertencia(null);
    cambiarPaso(paso);
  }

  function actualizar(campo, value) {
    const valor = campo === "caracteristicasParticulares"
      ? sanitizarTexto(value, { recortar: false })
      : value;
    setDatos((actuales) => ({ ...actuales, [campo]: valor }));
  }

  function seleccionarTipo(tipoMascota) {
    setDatos((actuales) => ({ ...actuales, tipoMascota, tamano: "" }));
  }

  function continuar(evento) {
    evento.preventDefault();
    const caracteristicas = sanitizarTexto(datos.caracteristicasParticulares);
    setDatos((actuales) => ({ ...actuales, caracteristicasParticulares: caracteristicas }));
    const faltantes = [];
    if (!datos.tipoMascota) faltantes.push("Tipo de mascota");
    if (!datos.sexo) faltantes.push("Sexo");
    if (!datos.tamano) faltantes.push("Tamaño");
    if (!datos.colorPrincipalPelaje) faltantes.push("Color principal del pelaje");
    if (!caracteristicas) faltantes.push("Características particulares");
    if (faltantes.length) {
      mostrarCamposFaltantes(faltantes, 1);
      return;
    }
    avanzarPaso(2);
  }

  function volver() {
    if (pasoActual > 1) {
      cambiarPaso(pasoActual - 1);
      return;
    }
    onBack();
  }

  function validarReporteCompleto() {
    const campos = [];
    let primerPaso = 4;
    if (!datos.sexo) { campos.push("Sexo"); primerPaso = Math.min(primerPaso, 1); }
    if (!datos.tamano) { campos.push("Tamaño"); primerPaso = Math.min(primerPaso, 1); }
    if (!datos.colorPrincipalPelaje) { campos.push("Color principal del pelaje"); primerPaso = Math.min(primerPaso, 1); }
    if (!sanitizarTexto(datos.caracteristicasParticulares)) { campos.push("Características particulares"); primerPaso = Math.min(primerPaso, 1); }
    if (!sanitizarTexto(datos.direccionHallazgo)) { campos.push("Dirección o referencia del hallazgo"); primerPaso = Math.min(primerPaso, 2); }
    if (datos.latEncontro == null || datos.lngEncontro == null) { campos.push("Ubicación exacta en el mapa"); primerPaso = Math.min(primerPaso, 2); }
    if (!fotos.length) { campos.push("Al menos una foto"); primerPaso = Math.min(primerPaso, 3); }
    if (!sanitizarTexto(contacto.nombre)) campos.push("Nombre de contacto");
    if (contacto.telefono.length !== 10) campos.push("WhatsApp de 10 dígitos");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizarEmail(contacto.email))) campos.push("Correo electrónico válido");

    setDatos((actuales) => ({
      ...actuales,
      caracteristicasParticulares: sanitizarTexto(actuales.caracteristicasParticulares),
      direccionHallazgo: sanitizarTexto(actuales.direccionHallazgo),
      puntoReferencia: sanitizarTexto(actuales.puntoReferencia),
    }));
    setContacto((actual) => ({ ...actual, nombre: sanitizarTexto(actual.nombre), email: sanitizarEmail(actual.email) }));

    if (campos.length) {
      mostrarCamposFaltantes(campos, primerPaso);
      return false;
    }
    return true;
  }

  /**
   * Publica el reporte y sube las fotos.
   *
   * Orden deliberado: primero se crea el reporte, despues las fotos. Asi un
   * fallo de subida no obliga a rellenar el formulario — el reporte ya existe y
   * las fotos se pueden agregar despues con el codigo de gestion.
   *
   * Las fotos van de a una y no en paralelo: en red precaria, cuatro subidas
   * compitiendo se degradan mutuamente, y el limite de escritura por minuto del
   * servidor las rechazaria.
   */
  async function publicar() {
    if (publicando) return;
    if (!validarReporteCompleto()) return;

    setPublicando(true);
    setErrorEnvio(null);
    setProgreso({ indice: 0, total: fotos.length, porcentaje: 0 });

    try {
      const reportePublicado = await publicarMascota({
        payload: construirPayload(datos, contacto),
        fotos,
        estado: publicacionRef.current,
        onProgreso: setProgreso, tokenAntiBot,
      });
      limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES);
      setResultado(reportePublicado);
    } catch (error) {
      setPublicando(false);
      setErrorEnvio(error);
      // El token de Turnstile se gasta en cada intento: hay que pedir uno
      // nuevo o el reintento vuelve a fallar con 403.
      setTokenAntiBot(null);
      turnstileRef.current?.reiniciar();
      return;
    }
    setPublicando(false);
  }

  if (resultado) {
    return (
      <div className="report-flow">
        <ReportePublicado resultado={resultado} datos={datos} fotos={fotos} contacto={contacto} onInicio={() => { limpiarBorrador(STORAGE_KEY, STORAGE_KEYS_ANTERIORES); onBack(); }} />
      </div>
    );
  }

  let pasoPosterior;
  if (pasoActual === 2) {
    pasoPosterior = <UbicacionMascotaForm datos={datos} onChange={setDatos} onBack={() => cambiarPaso(1)} onNext={() => avanzarPaso(3)} onMissing={(campos) => mostrarCamposFaltantes(campos, 2)} />;
  } else if (pasoActual === 3) {
    pasoPosterior = <FotosMascotaForm fotos={fotos} onChange={setFotos} onBack={() => cambiarPaso(2)} onNext={() => avanzarPaso(4)} onMissing={(campos) => mostrarCamposFaltantes(campos, 3)} />;
  } else {
    pasoPosterior = <ConfirmarMascotaForm datos={datos} fotos={fotos} contacto={contacto} onContactChange={setContacto} onBack={() => cambiarPaso(3)} onEditStep={cambiarPaso} onPublicar={publicar} widgetAntiBot={<Turnstile ref={turnstileRef} onToken={setTokenAntiBot} onError={(m) => setErrorEnvio(new Error(m))} onExpirado={() => setTokenAntiBot(null)} />} publicando={publicando} progreso={progreso} errorEnvio={errorEnvio} />;
  }

  return (
    <div className="report-flow">
      <header className="report-flow__header">
        <button type="button" onClick={volver} aria-label={pasoActual === 1 ? "Volver al inicio" : "Volver al paso anterior"}><ArrowLeftIcon weight="bold" /></button>
        <h1>Reportar mascota encontrada</h1>
      </header>

      <ol className="stepper" aria-label="Progreso del reporte">
        {pasos.map((paso, index) => (
          <li className={clasePaso(index + 1, pasoActual, maximoPasoAlcanzado)} aria-current={index + 1 === pasoActual ? "step" : undefined} key={paso}>
            <button type="button" onClick={() => cambiarPaso(index + 1)} disabled={index + 1 > maximoPasoAlcanzado} aria-label={`Ir a ${paso}`}>
              {index + 1 < pasoActual ? <CheckIcon weight="bold" /> : index + 1}
            </button>
            <strong>{paso}</strong>
          </li>
        ))}
      </ol>

      {pasoActual === 1 ? <>{borradorRestaurado && <AvisoBorrador onDescartar={descartarBorrador} />}
      <div className="report-intro">
        <span><PawPrintIcon weight="fill" /></span>
        <h2>Cuéntanos sobre<br />la mascota</h2>
        <p>Toda la información que brindes puede ayudar a reunirla con su familia.</p>
      </div>

      <form className="pet-form" onSubmit={continuar}>
        <fieldset className="pet-type">
          <legend>Tipo de mascota</legend>
          <div className="pet-type__grid">
            {TIPO_MASCOTA.map((tipo) => {
              const Icon = iconosTipo[tipo];
              return (
                <button
                  className={datos.tipoMascota === tipo ? "is-selected" : ""}
                  type="button"
                  aria-pressed={datos.tipoMascota === tipo}
                  onClick={() => seleccionarTipo(tipo)}
                  key={tipo}
                >
                  <Icon weight="bold" /><span>{etiquetasTipo[tipo]}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="basic-fields">
          <legend>Descripción básica</legend>
          <label>Sexo
            <span className="select-wrap"><select value={datos.sexo} onChange={(e) => actualizar("sexo", e.target.value)}><option value="">Selecciona</option>{SEXO_MASCOTA.map((sexo) => <option value={sexo} key={sexo}>{etiquetasSexo[sexo]}</option>)}</select><CaretDownIcon weight="bold" /></span>
          </label>

          <label>Tamaño</label>
          <SelectorTamano tipoMascota={datos.tipoMascota} value={datos.tamano} onChange={(value) => actualizar("tamano", value)} />

          <label>Color principal del pelaje
            <span className="select-wrap"><select value={datos.colorPrincipalPelaje} onChange={(e) => actualizar("colorPrincipalPelaje", e.target.value)}><option value="">Selecciona</option>{COLOR_PELAJE.map((color) => <option value={color} key={color}>{etiquetasColor[color]}</option>)}</select><CaretDownIcon weight="bold" /></span>
          </label>

          <label>Características particulares
            <textarea maxLength="500" rows="4" value={datos.caracteristicasParticulares} onChange={(e) => actualizar("caracteristicasParticulares", e.target.value)} placeholder="Ej: tiene una mancha blanca en el pecho, cola rizada, cojea de una pata…" />
            <small className="character-count">{datos.caracteristicasParticulares.length}/500</small>
          </label>
        </fieldset>

        <fieldset className="pet-status">
          <legend>Estado de la mascota</legend>
          <div>
            {estados.filter(({ value }) => ESTADO_MASCOTA.includes(value)).map(({ value, label, Icon }) => (
              <button className={`pet-status__${value.toLowerCase()} ${datos.estadoMascota === value ? "is-selected" : ""}`} type="button" aria-pressed={datos.estadoMascota === value} onClick={() => actualizar("estadoMascota", value)} key={value}><Icon weight="bold" />{label}</button>
            ))}
          </div>
        </fieldset>

        <aside className="review-note"><ShieldCheckIcon weight="duotone" /><span>Tu reporte será revisado y publicado para ayudar a encontrar a su familia.</span></aside>
        <button className="continue-button" type="submit">Siguiente <ArrowRightIcon weight="bold" /></button>
      </form></> : pasoPosterior}

      <nav className="flow-nav" aria-label="Navegación principal">
        <button type="button" onClick={onBack}><HouseIcon weight="fill" /><span>Inicio</span></button>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><ListIcon /><span>Reportes</span></span>
        <a className="is-active" href="#reportar"><span className="flow-nav__add"><PlusIcon weight="bold" /></span><span>Reportar</span></a>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><BellIcon /><span>Alertas</span></span>
        <span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><UserCircleIcon /><span>Mi cuenta</span></span>
      </nav>
      {advertencia && <ModalCamposFaltantes campos={advertencia.campos} onClose={() => setAdvertencia(null)} onReview={revisarCamposFaltantes} />}
    </div>
  );
}

export default MascotaEncontradaForm;
